import React, { useState } from 'react';
import { apiService } from '../services/api';

interface JoinPublicLeagueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  leagueId: number;
  leagueName: string;
  currentParticipants: number;
  maxParticipants: number;
}

const JoinPublicLeagueModal: React.FC<JoinPublicLeagueModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  leagueId,
  leagueName,
  currentParticipants,
  maxParticipants
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleJoin = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await apiService.joinPickemLeague(leagueId);
      
      if (data.success) {
        setSuccess(data.message || 'Successfully joined the league!');
        
        // Close modal after 2 seconds and refresh leagues
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Failed to join league');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to join league');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Join Public League</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* League Info */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-2">{leagueName}</h3>
            <div className="text-sm text-gray-300 space-y-1">
              <p>Participants: {currentParticipants}/{maxParticipants}</p>
              <p>Type: Public League</p>
            </div>
          </div>

          {/* Warning if league is almost full */}
          {currentParticipants >= maxParticipants * 0.9 && (
            <div className="bg-yellow-900 bg-opacity-20 border border-yellow-500 text-yellow-300 p-3 rounded-md">
              <p className="text-sm">
                ⚠️ This league is almost full! Only {maxParticipants - currentParticipants} spots remaining.
              </p>
            </div>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-900 bg-opacity-20 p-3 rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="text-green-400 text-sm bg-green-900 bg-opacity-20 p-3 rounded-md">
              {success}
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleJoin}
              disabled={loading || currentParticipants >= maxParticipants}
              className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                loading || currentParticipants >= maxParticipants
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? 'Joining...' : currentParticipants >= maxParticipants ? 'League Full' : 'Join League'}
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-700 rounded-md">
          <p className="text-sm text-gray-300">
            <strong>Public League:</strong>
          </p>
          <ul className="text-xs text-gray-400 mt-2 space-y-1">
            <li>• No join code required</li>
            <li>• Anyone can join if spots are available</li>
            <li>• League will appear in "My Leagues" after joining</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JoinPublicLeagueModal; 